import { ArrowBigLeft, ArrowBigRight } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import Carousel from "../common/Carousel";
import styles from "../dashboard/Dashboard.module.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchDataVisData } from "src/thunks/dashboardThunk";
import { useStatusDelay } from "../common/useStatusDelay";
import { Doughnut, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Colors,
} from "chart.js";
import { naruhoneStyles } from "src/styles/naruhoneStyles";

ChartJS.register(ArcElement, Tooltip);

const DataVisWidget = (props) => {
  const wanikaniData = useSelector((state) => state.dashboard.dataVis);
  const dispatch = useDispatch();

  const { delayedStatus, isDelaying } = useStatusDelay(
    wanikaniData.status,
    800
  );

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [tooltipIndexes, setTooltipIndexes] = useState({ 0: 0, 1: 0 }); // Per-slice index
  const intervalRef = useRef(null);
  const chartRef = useRef();
  const [passedKanjiList, setPassedKanjiList] = useState([
    { characters: "水", meaning: "water" },
    { characters: "火", meaning: "fire" },
    { characters: "木", meaning: "tree" },
  ]);
  const [recentlyPassedKanjiList, setRecentlyPassedKanjiList] = useState([]);
  const [learningKanjiList, setLearningKanjiList] = useState([
    { characters: "山", meaning: "mountain" },
    { characters: "川", meaning: "river" },
    { characters: "空", meaning: "sky" },
  ]);

  const currentLevelPercentageRef = useRef();

  const glowSegmentIndex = 1; // e.g. second segment

  const GlowPlugin = {
    id: "glowSegment",
    afterDraw(chart) {
      const ctx = chart.ctx;
      const arc = chart.getDatasetMeta(0).data[glowSegmentIndex];
      const dataset = chart.data.datasets[0];
      if (!arc || !dataset) return;

      const glowColor = "#FF00AA";
      const time = Date.now() / 500;
      const blur = 10 + 20 * Math.abs(Math.sin(time));

      ctx.save();

      // Set fill color to match the actual arc color
      ctx.fillStyle = dataset.backgroundColor[glowSegmentIndex];

      ctx.shadowColor = glowColor;
      ctx.shadowBlur = blur;

      ctx.beginPath();
      arc.draw(ctx);
      ctx.fill();

      ctx.restore();
    },
  };

  useEffect(() => {
    if (hoveredIndex === 0 || hoveredIndex === 1 || hoveredIndex === 2) {
      intervalRef.current = setInterval(() => {
        setTooltipIndexes((prev) => {
          const currentList =
            hoveredIndex === 0 || hoveredIndex === 1
              ? passedKanjiList
              : learningKanjiList;
          return {
            ...prev,
            [hoveredIndex === 0 || hoveredIndex === 1 ? 0 : 1]:
              (prev[hoveredIndex === 0 || hoveredIndex === 1 ? 0 : 1] + 1) %
              currentList.length,
          };
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [hoveredIndex]);

  function easeInOutQuint(x) {
    return x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2;
  }
  const [pieValue, setPieValue] = useState(0);
  useEffect(() => { }, []);

  function symmetricDifference(arr1, arr2, key) {
    const set1 = new Set(arr1.map((item) => item[key]));
    const set2 = new Set(arr2.map((item) => item[key]));

    const unique1 = arr1.filter((item) => !set2.has(item[key]));
    const unique2 = arr2.filter((item) => !set1.has(item[key]));

    return [...unique1, ...unique2];
  }
  function difference(arr1, arr2, key) {
    const set2 = new Set(arr2.map((item) => item[key]));
    return arr1.filter((item) => !set2.has(item[key]));
  }

  useEffect(() => {
    if (wanikaniData.status === "idle") {
      currentLevelPercentageRef.current = 0;
      dispatch(fetchDataVisData());
    }
    if (wanikaniData.status === "fulfilled") {
      let wanikaniDataLatest =
        wanikaniData.data?.progressions[
          wanikaniData.data?.progressions?.length - 1
        ]?.currentLevelWords;
      let wanikaniDataOldest =
        wanikaniData.data?.progressions[0]?.currentLevelWords;

      let recentlyPassedWords = wanikaniDataLatest.filter((item) => {
        return item.passed === true;
      });
      let oldPassedWords = wanikaniDataOldest.filter((item) => {
        return item.passed === true;
      });
      recentlyPassedWords = symmetricDifference(
        recentlyPassedWords,
        oldPassedWords,
        "characters"
      );

      setRecentlyPassedKanjiList(recentlyPassedWords);

      setPassedKanjiList(
        difference(
          wanikaniData.data?.progressions[
            wanikaniData.data?.progressions.length - 1
          ]?.currentLevelWords.filter((clw) => clw.passed),
          recentlyPassedWords,
          "characters"
        )
      );
      setLearningKanjiList(
        wanikaniData.data?.progressions[
          wanikaniData.data?.progressions.length - 1
        ]?.currentLevelWords.filter((clw) => !clw.passed)
      );
    }
  }, [wanikaniData, dispatch]);

  const getSortedWanikaniData = () => {
    let convertedData = {
      passed: 0,
      learning: 0,
      old: 0,
    };

    let wanikaniDataLatest =
      wanikaniData.data?.progressions[
        wanikaniData.data?.progressions?.length - 1
      ]?.currentLevelWords;
    let wanikaniDataOldest =
      wanikaniData.data?.progressions[0]?.currentLevelWords;
    wanikaniDataOldest?.map((clw) => {
      if (clw.passed) {
        convertedData.old++;
      }
    });
    wanikaniDataLatest?.map((clw) => {
      if (clw.passed) {
        convertedData.passed++;
      } else {
        convertedData.learning++;
      }
    });

    convertedData.passed = convertedData.passed - convertedData.old;

    return convertedData;
  };
  const convertWanikaniData = () => {
    let convertedData = getSortedWanikaniData();

    let wanikaniDataLatest =
      wanikaniData.data?.progressions[
        wanikaniData.data?.progressions?.length - 1
      ]?.currentLevelWords;
    let totalLevelWords = wanikaniDataLatest.length;
    currentLevelPercentageRef.current = Math.trunc(
      ((convertedData.passed + convertedData.old) / totalLevelWords) * 100
    );
    let data = [
      {
        label: "Total progress",
        value: convertedData.old,
        color: "#6f00ff",
        cutout: "50%",
      },
      {
        label: "Progress this week",
        value: convertedData.passed,
        color: "#FF00AA",
        cutout: "50%",
      },
      {
        label: "Still learning",
        value: convertedData.learning,
        color: "#333",
        cutout: "50%",
      },
    ];

    const finalData = {
      labels: data?.map((item) => item.label),
      datasets: [
        {
          data: data.map((item) => Math.round(item.value)),
          backgroundColor: data.map((item) => item.color),
          borderColor: data.map((item) => item.color),
          borderWidth: 1,
          dataVisibility: new Array(data.length).fill(true),
        },
      ],
    };
    return finalData;
  };

  const testDispatch = () => {
    dispatch(fetchDataVisData());
  };

  useEffect(() => {
    let animationFrameId;

    const render = () => {
      if (chartRef.current) {
        chartRef.current.draw(); // For react-chartjs-2 v3+, use chartRef.current.draw()
        // In react-chartjs-2 v4, it is chartRef.current.chart.draw()
      }
      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <Carousel>
      <div
        className={`h-full transition-opacity duration-1000 ${isDelaying ? "opacity-0" : "opacity-100"
          }`}
      >
        {/* <p className="text-base-content/70">Wanikani progress {wanikaniData.status}</p> */}
        {delayedStatus === "pending" && (
          <span className=" w-100px h-100px loading loading-spinner text-primary loading-xl"></span>
        )}
        {delayedStatus === "fulfilled" && (
          <div className="flex flex-col h-full">
            <div className="flex justify-center pt-4">
              <div
                className="w-32 h-12
                 bg-white"
                style={{
                  WebkitMaskImage: "url('/wanikani-logo.png')",
                  WebkitMaskSize: "contain",
                  WebkitMaskRepeat: "no-repeat",
                  WebkitMaskPosition: "center",
                  maskImage: "url('/wanikani-logo.png')",
                  maskSize: "contain",
                  maskRepeat: "no-repeat",
                  maskPosition: "center",
                }}
              ></div>
            </div>
            <div className="flex-1 h-full relative">
              <div className="h-full flex items-center justify-center">
                <div className="flex-1 w-full max-w-[200px] max-h-[200px]">
                  <Doughnut
                    ref={chartRef}
                    data={convertWanikaniData()}
                    plugins={[GlowPlugin]}
                    options={{
                      onHover: (_, elements) => {
                        if (elements.length > 0) {
                          setHoveredIndex(elements[0].index);
                        } else {
                          setHoveredIndex(null);
                        }
                      },
                      cutout: "70%",
                      animation: false,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false, // <-- this enables the legend
                        },
                        tooltip: {
                          callbacks: {
                            label: function (context) {
                              if (hoveredIndex === 0 || hoveredIndex === 1) {
                                const item = passedKanjiList[tooltipIndexes[0]];
                                return [
                                  `${passedKanjiList.length + recentlyPassedKanjiList.length} words.`,
                                  `${tooltipIndexes[0] + 1}: ${item.characters
                                  } – ${item.meaning}`,
                                ];
                              } else if (hoveredIndex === 2) {
                                const item =
                                  learningKanjiList[tooltipIndexes[1]];
                                return [
                                  `${learningKanjiList.length} words.`,
                                  `${tooltipIndexes[1] + 1}: ${item.characters
                                  } – ${item.meaning}`,
                                ];
                              }
                              return "";
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
                <div className="z-[-10] flex-col absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <h2 className=" text-secondary font-bold pointer-events-none">
                    Level {wanikaniData?.data?.currentLevel}
                  </h2>
                  <h2 className=" text-secondary font-bold pointer-events-none">
                    {currentLevelPercentageRef.current}%
                  </h2>
                </div>
              </div>
            </div>
          </div>
        )}
        {delayedStatus === "rejected" && <p className="text-error">ERROR</p>}
      </div>

      <div
        className={`h-full transition-opacity duration-1000 overflow-y-auto p-4 ${isDelaying ? "opacity-0" : "opacity-100"
          }`}
      >
        <p
          className={`w-full text-center text-xl mb-2 ${naruhoneStyles.h2Text}`}
        >
          Level {wanikaniData?.data?.currentLevel} words
        </p>
        <div className="flex flex-wrap text-white ">
          {passedKanjiList !== null &&
            passedKanjiList?.map((item, idx) => {
              return (
                <div
                  key={idx}
                  title={item.meaning}
                  className={`bg-[#6f00ff] mr-2 mb-2 rounded-sm p-1`}
                >
                  {item.characters}
                </div>
              );
            })}
          {recentlyPassedKanjiList !== null &&
            recentlyPassedKanjiList?.map((item, idx) => {
              return (
                <div
                  key={idx}
                  title={item.meaning}
                  className={`bg-[#FF00AA] mr-2 mb-2 rounded-sm p-1`}
                >
                  {item.characters}
                </div>
              );
            })}
          {learningKanjiList !== null &&
            learningKanjiList?.map((item, idx) => {
              return (
                <div key={idx}
                  title={item.meaning}
                  className={`bg-[#333] mr-2 mb-2 rounded-sm p-1`}>
                  {item.characters}
                </div>
              );
            })}
        </div>
      </div>

      <div className="flex flex-col h-full">
        <div className="text-primary-content text-center py-4">
          Pie chart of countries of languages I'm learning
        </div>
        <div className="flex-1 flex justify-center py-4 mx-4 bg-white items-center relative">
          <div className="w-full max-w-[100px] max-h-[100px]">
            <Pie
              data={{
                labels: ["Japan"],
                datasets: [
                  {
                    data: [100],
                    backgroundColor: ["#ff0000"],
                    borderColor: ["#ff0000"],
                    borderWidth: 1,
                    dataVisibility: [true],
                  },
                ],
              }}
              options={{
                responsive: true,
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return "";
                      },
                    },
                  },
                  legend: {
                    display: true, // <-- this enables the legend
                    position: "right", // top, bottom, left, right
                  },
                },
              }}
            />
          </div>
        </div>
        <div
          className={`absolute h-full right-0 inset-y-0 align-center size-32`}
        >
          <div className="flex-col items-center justify-center h-full">
            <div className="flex items-center justify-center h-full mt-8">
              <div className="bg-red-500 w-4 h-4 mr-2"></div>
              <p className={`text-black `}>Japan</p>
            </div>
          </div>
        </div>
      </div>
    </Carousel>
  );
};

export default DataVisWidget;
